/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2025 HG Digital/Setera
 * Todos os direitos reservados.
 * 
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 30-06-2025
 * Autor     : diegobrock
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, timer } from 'rxjs';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { HateoasBaseService } from './hateoas-base.service';
import { HttpUtil } from '../utils/http-util';
import { PageEvent } from '@angular/material/paginator';
import { environment } from '../../environments/environment';
import {
  CreateStreamingSessionRequest,
  StreamingSessionResponse,
  IStreamingSession,
  StreamingSessionStatus,
  StreamingSessionListResponse,
  StreamingSessionFilters,
  StreamingSessionStats
} from '../models/streaming-session.model';
import { toSnake, toCamel } from 'snake-camel';

@Injectable({
  providedIn: 'root'
})
export class StreamingSessionService extends HateoasBaseService {
  private resourceName = 'streaming-sessions';
  private _httpUtil: HttpUtil;
  
  private sessionsSubject = new BehaviorSubject<IStreamingSession[]>([]);
  private activeSessionSubject = new BehaviorSubject<IStreamingSession | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);

  public sessions$ = this.sessionsSubject.asObservable();
  public activeSession$ = this.activeSessionSubject.asObservable();
  public loading$ = this.loadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();

  private refreshTimer: any;
  private readonly refreshInterval = 5000; // 5 segundos

  constructor(http: HttpClient, httpUtil: HttpUtil) {
    super(http, httpUtil);
    this._httpUtil = httpUtil;
    // O auto-refresh NÃO é mais ativado por padrão. Ative manualmente apenas em componentes de listagem.
    // this.startAutoRefresh();
  }

  /**
   * Cria uma nova sessão de streaming
   */
  public createStreamingSession(request: CreateStreamingSessionRequest): Observable<IStreamingSession> {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.executeActionOnResource(
      this.resourceName,
      'create',
      null,
      toSnake(request),
      null
    ).pipe(
      map((response: any) => this.mapResponseToSession(toCamel(response) as StreamingSessionResponse)),
      tap(session => {
        this.addSessionToList(session);
        this.activeSessionSubject.next(session);
      }),
      catchError(error => {
        this.errorSubject.next(this.handleError(error));
        throw error;
      }),
      tap(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Obtém uma sessão específica por UUID
   */
  public getStreamingSession(sessionUuid: string): Observable<IStreamingSession> {
    const params = new Map<string, string>();
    params.set('uuid', sessionUuid);

    return this.executeActionOnResource(
      this.resourceName,
      'get-by-uuid',
      null,
      null,
      params
    ).pipe(
      map((response: any) => this.mapResponseToSession(toCamel(response) as StreamingSessionResponse))
    );
  }

  /**
   * Obtém todas as sessões do usuário atual usando busca dinâmica
   */
  public getUserSessions(filters?: StreamingSessionFilters): Observable<IStreamingSession[]> {
    this.loadingSubject.next(true);
    
    const searchQuery = this.buildSearchQuery(filters);
    const params = new Map<string, string>();
    
    if (searchQuery) {
      params.set('search', searchQuery);
    }

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
      tap(sessions => {
        this.sessionsSubject.next(sessions);
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        this.errorSubject.next(this.handleError(error));
        this.loadingSubject.next(false);
        throw error;
      })
    );
  }

  /**
   * Obtém sessões com paginação usando busca dinâmica
   */
  public getSessions(page: PageEvent, filters?: StreamingSessionFilters): Observable<StreamingSessionListResponse> {
    const sort = 'createdAt,DESC';
    const params = this._httpUtil.buildPaginator(page, sort);
    
    const searchQuery = this.buildSearchQuery(filters);
    if (searchQuery) {
      params.set('search', searchQuery);
    }

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    );
  }

  /**
   * Obtém sessões ativas usando busca dinâmica
   */
  public getActiveSessions(): Observable<IStreamingSession[]> {
    const searchQuery = 'status:A';
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
    );
  }

  /**
   * Obtém sessões pendentes usando busca dinâmica
   */
  public getPendingSessions(): Observable<IStreamingSession[]> {
    const searchQuery = 'status:P';
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
    );
  }

  /**
   * Obtém sessões por SIM card usando busca dinâmica
   */
  public getSessionsBySimCard(simCard: string): Observable<IStreamingSession[]> {
    const searchQuery = `simCard:${simCard}`;
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
    );
  }

  /**
   * Obtém sessões por terminal ID usando busca dinâmica
   */
  public getSessionsByTerminalId(terminalId: number): Observable<IStreamingSession[]> {
    const searchQuery = `terminalId:${terminalId}`;
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
    );
  }

  /**
   * Obtém sessões por usuário usando busca dinâmica
   */
  public getSessionsByUserId(userId: number): Observable<IStreamingSession[]> {
    const searchQuery = `userId:${userId}`;
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
    );
  }

  /**
   * Obtém sessões não expiradas usando busca dinâmica
   */
  public getNonExpiredSessions(): Observable<IStreamingSession[]> {
    const now = new Date().toISOString();
    const searchQuery = `expiresAt>${now}`;
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
    );
  }

  /**
   * Obtém sessões criadas após uma data usando busca dinâmica
   */
  public getSessionsCreatedAfter(date: Date): Observable<IStreamingSession[]> {
    const dateStr = date.toISOString();
    const searchQuery = `createdDate>${dateStr}`;
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: StreamingSessionListResponse) => response.content.map(session => this.mapResponseToSession(session)))
    );
  }

  /**
   * Obtém sessões criadas antes de uma data usando busca dinâmica
   */
  public getSessionsCreatedBefore(date: Date): Observable<IStreamingSession[]> {
    const dateStr = date.toISOString();
    const searchQuery = `createdDate<${dateStr}`;
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
    );
  }

  /**
   * Obtém sessões por status usando busca dinâmica
   */
  public getSessionsByStatus(status: StreamingSessionStatus): Observable<IStreamingSession[]> {
    const searchQuery = `status:${status}`;
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: StreamingSessionListResponse) => response.content.map(session => this.mapResponseToSession(session)))
    );
  }

  /**
   * Obtém sessões ativas ou pendentes usando busca dinâmica
   */
  public getActiveOrPendingSessions(): Observable<IStreamingSession[]> {
    const searchQuery = "'status:A'OR'status:P'";
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
        map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
    );
  }

  /**
   * Obtém sessões por SIM card e status usando busca dinâmica
   */
  public getSessionsBySimCardAndStatus(simCard: string, status: StreamingSessionStatus): Observable<IStreamingSession[]> {
    const searchQuery = `simCard:${simCard},status:${status}`;
    const params = new Map<string, string>();
    params.set('search', searchQuery);

    return this.executeActionOnResource(
      this.resourceName,
      'get-all',
      null,
      null,
      params
    ).pipe(
      map((response: any) => response.content.map((session: any) => this.mapResponseToSession(toCamel(session) as StreamingSessionResponse))),
    );
  }

  /**
   * Constrói string de busca dinâmica baseada nos filtros
   */
  private buildSearchQuery(filters?: StreamingSessionFilters): string {
    if (!filters) {
      return '';
    }

    const searchParts: string[] = [];

    // Filtro por status
    if (filters.status) {
      searchParts.push(`status:${filters.status}`);
    }

    // Filtro por SIM card
    if (filters.simCard) {
      searchParts.push(`simCard:${filters.simCard}`);
    }

    // Filtro por terminal ID
    if (filters.terminalId) {
      searchParts.push(`terminalId:${filters.terminalId}`);
    }

    // Filtro por usuário
    if (filters.userId) {
      searchParts.push(`userId:${filters.userId}`);
    }

    // Filtro por data de início
    if (filters.startedAt) {
      searchParts.push(`createdDate>${filters.startedAt}`);
    }

    // Filtro por data de fim
    if (filters.expiresAt) {
      searchParts.push(`createdDate<${filters.expiresAt}`);
    }

    return searchParts.join(',');
  }

  /**
   * Cancela uma sessão de streaming
   */
  public cancelStreamingSession(sessionUuid: string): Observable<void> {
    const params = new Map<string, string>();
    params.set('sessionUuid', sessionUuid);

    return this.executeActionOnResource(
      this.resourceName,
      'cancel',
      null,
      null,
      params
    ).pipe(
      tap(() => {
        this.removeSessionFromList(sessionUuid);
        if (this.activeSessionSubject.value?.uuid === sessionUuid) {
          this.activeSessionSubject.next(null);
        }
      })
    );
  }

  /**
   * Obtém estatísticas das sessões
   */
  public getSessionStats(): Observable<StreamingSessionStats> {
    return this.executeActionOnResource(
      this.resourceName,
      'stats',
      null,
      null,
      null
    );
  }

  /**
   * Atualiza o status de uma sessão
   */
  public updateSessionStatus(sessionUuid: string, status: StreamingSessionStatus): Observable<IStreamingSession> {
    const params = new Map<string, string>();
    params.set('sessionUuid', sessionUuid);

    return this.executeActionOnResource(
      this.resourceName,
      'update-status',
      null,
      { status },
      params
    ).pipe(
      map((response: any) => this.mapResponseToSession(toCamel(response) as StreamingSessionResponse)),
      tap(session => this.updateSessionInList(session))
    );
  }

  /**
   * Gera URL de streaming segura com sessão
   * PRIORIDADE 1: Usar streamUrl da API se disponível
   * PRIORIDADE 2: Fallback para geração local (comportamento atual)
   */
  public generateSecureStreamUrl(session: IStreamingSession, protocol: 'hls' | 'flv' | 'webrtc'): string {
    // PRIORIDADE 1: Usar streamUrl da API se disponível
    if (session.streamUrl) {
      console.info('[StreamingSessionService] Usando streamUrl da API:', session.streamUrl);
      return session.streamUrl;
    }
    
    // PRIORIDADE 2: Fallback para geração local (comportamento atual)
    console.info('[StreamingSessionService] Usando fallback local para geração de URL');
    const srsConfig = environment.apisConfig.seteraStreaming.srs;
    // Usa exclusivamente o UUID da sessão como nome da stream
    const streamName = session.uuid;
    
    switch (protocol) {
      case 'hls':
        return `${srsConfig.hlsUrl}/${streamName}.m3u8`;
      case 'flv':
        return `${srsConfig.flvUrl}/${streamName}.flv`;
      case 'webrtc':
        return `${srsConfig.webrtcUrl}?streamurl=webrtc://${srsConfig.host}/${srsConfig.app}/${streamName}`;
      default:
        return `${srsConfig.hlsUrl}/${streamName}.m3u8`;
    }
  }

  /**
   * Verifica se uma sessão está ativa
   */
  public isSessionActive(session: IStreamingSession): boolean {
    return session.status === StreamingSessionStatus.ACTIVE && !session.isExpired;
  }

  /**
   * Obtém sessão ativa para um terminal específico
   */
  public getActiveSessionForTerminal(terminalId: number): IStreamingSession | null {
    return this.sessionsSubject.value.find(session => 
      session.terminalId === terminalId && this.isSessionActive(session)
    ) || null;
  }

  /**
   * Inicia refresh automático das sessões
   */
  private startAutoRefresh(): void {
    this.refreshTimer = timer(0, this.refreshInterval).pipe(
      switchMap(() => this.getUserSessions())
    ).subscribe();
  }

  /**
   * Para o refresh automático
   */
  public stopAutoRefresh(): void {
    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
  }

  /**
   * Mapeia resposta da API para interface do frontend
   */
  private mapResponseToSession(response: StreamingSessionResponse): IStreamingSession {
    return {
      uuid: response.uuid,
      simCard: response.simCard,
      terminalId: response.terminalId,
      userId: response.userId,
      status: mapBackendStatus(response.status),
      protocol: response.protocol,
      streamName: response.streamName,
      streamUrl: response.streamUrl, // NOVO: incluir streamUrl da API
      logicalChannel: response.logicalChannel, // NOVO: incluir logicalChannel da API
      createdDate: parseDate(response.createdDate),
      startedAt: response.startedAt ? parseDate(response.startedAt) : undefined,
      expiresAt: response.expiresAt ? parseDate(response.expiresAt) : undefined,
      duration: response.duration,
      description: response.description,
      isExpired: false,
      timeRemaining: 0
    };
  }

  /**
   * Adiciona sessão à lista local
   */
  private addSessionToList(session: IStreamingSession): void {
    const currentSessions = this.sessionsSubject.value;
    const existingIndex = currentSessions.findIndex(s => s.uuid === session.uuid);
    
    if (existingIndex >= 0) {
      currentSessions[existingIndex] = session;
    } else {
      currentSessions.push(session);
    }
    
    this.sessionsSubject.next([...currentSessions]);
  }

  /**
   * Remove sessão da lista local
   */
  private removeSessionFromList(sessionUuid: string): void {
    const currentSessions = this.sessionsSubject.value.filter(s => s.uuid !== sessionUuid);
    this.sessionsSubject.next(currentSessions);
  }

  /**
   * Atualiza sessão na lista local
   */
  private updateSessionInList(session: IStreamingSession): void {
    const currentSessions = this.sessionsSubject.value.map(s => 
      s.uuid === session.uuid ? session : s
    );
    this.sessionsSubject.next(currentSessions);
  }

  /**
   * Trata erros da API
   */
  private handleError(error: any): string {
    if (error.error?.message) {
      return error.error.message;
    } else if (error.message) {
      return error.message;
    } else {
      return 'Erro desconhecido ao processar a requisição';
    }
  }

  /**
   * Limpa dados do serviço
   */
  public clearData(): void {
    this.sessionsSubject.next([]);
    this.activeSessionSubject.next(null);
    this.errorSubject.next(null);
  }

  /**
   * Implementação dos métodos abstratos do HateoasBaseService
   */
  protected getApiBaseUrl(): string {
    return environment.apisConfig.seteraStreaming.apiBaseUrl;
  }

  protected getMicroServiceBaseUrl(): string {
    return environment.apisConfig.seteraStreaming.microServiceBaseUrl;
  }
}

function mapBackendStatus(status: string): StreamingSessionStatus {
  switch (status) {
    case 'A': return StreamingSessionStatus.ACTIVE;
    case 'P': return StreamingSessionStatus.PENDING;
    case 'C': return StreamingSessionStatus.COMPLETED;
    case 'X': return StreamingSessionStatus.CANCELLED;
    case 'E': return StreamingSessionStatus.EXPIRED;
    default: return status as StreamingSessionStatus;
  }
}

function parseDate(d: any): Date | undefined {
  if (!d) return undefined;
  const date = new Date(d);
  return isNaN(date.getTime()) ? undefined : date;
} 